
import { UseFormRegister, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { DatePicker } from '@/components/ui/date-picker';
import type { Application } from '@/types/database.types';
import { useEffect } from 'react';

interface WorkersCompInformationProps {
  register: UseFormRegister<Partial<Application>>;
  watch: UseFormWatch<Partial<Application>>;
  setValue: UseFormSetValue<Partial<Application>>;
  data: Partial<Application>;
}

export const WorkersCompInformation = ({
  register,
  watch,
  setValue,
  data
}: WorkersCompInformationProps) => {
  useEffect(() => {
    // Initialize values on component mount only
    if (data) {
      // Handle current_workers_comp_carrier initialization 
      setValue('current_workers_comp_carrier', data.current_workers_comp_carrier === null ? '' : data.current_workers_comp_carrier || '');
      
      if (data.workers_comp_renewal_date) {
        setValue('workers_comp_renewal_date', data.workers_comp_renewal_date);
      }
      
      // Ensure boolean fields have proper boolean values
      setValue('is_workers_comp_trust', Boolean(data.is_workers_comp_trust));
    }
  }, [data, setValue]);

  // Helper function to handle date conversion
  const handleDateSelect = (date: Date | undefined, fieldName: string) => {
    if (date) {
      // Format as ISO string for database storage
      setValue(fieldName as any, date.toISOString());
    } else {
      setValue(fieldName as any, undefined);
    }
  };

  // Parse the date from string to Date object for the DatePicker
  const parseDate = (dateString: string | null | undefined): Date | undefined => {
    if (!dateString) return undefined;
    const date = new Date(dateString);
    return isNaN(date.getTime()) ? undefined : date;
  };

  // Get the current value from the form
  const workersCompRenewalDate = watch('workers_comp_renewal_date');
  const parsedDate = parseDate(workersCompRenewalDate);

  const isWorkersTrust = watch('is_workers_comp_trust');

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="current_workers_comp_carrier">Current Workers Comp Carrier</Label>
        <Input
          id="current_workers_comp_carrier"
          placeholder="Enter current workers' compensation carrier"
          {...register('current_workers_comp_carrier')}
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="workers_comp_renewal_date">Workers Comp Renewal Date</Label>
        <DatePicker
          id="workers_comp_renewal_date"
          date={parsedDate}
          onSelect={(date) => handleDateSelect(date, 'workers_comp_renewal_date')}
          placeholder="Select renewal date"
        />
      </div>

      <div className="flex items-center space-x-2 mt-4">
        <Checkbox
          id="is_workers_comp_trust"
          checked={Boolean(isWorkersTrust)}
          onCheckedChange={(checked) => setValue('is_workers_comp_trust', Boolean(checked))}
        />
        <Label htmlFor="is_workers_comp_trust" className="cursor-pointer">
          Part of workers' compensation trust
        </Label>
      </div>
    </div>
  );
};
